import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomElementProps } from '../../types/react-props-types';
import { KeyCodes } from '../../utils/dom-utils';
import './TableCellTextBox.scss';

interface TableCellTextBoxProps extends CustomElementProps {
  disabled?: boolean;
  readonly?: boolean;
  initialValue: string | undefined;
  placeholder?: string;
  maxLength?: number;
  focus?: boolean;
  onCancel: () => void;
  onSave: (value: string) => void;
  'data-testid'?: string;
}

const TableCellTextBox = (props: TableCellTextBoxProps): React.JSX.Element => {
  const {
    id,
    onCancel,
    onSave,
    initialValue,
    className,
    maxLength,
    placeholder,
    disabled,
    readonly,
    ariaLabel,
    'data-testid': dataTestId,
    focus,
  } = props;
  const [value, setValue] = useState<string>(initialValue || '');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focus) {
      ref.current?.focus();
    }
  }, [focus]);

  const doSave = useCallback(() => {
    if (value !== initialValue) {
      onSave(value);
    } else {
      onCancel();
    }
  }, [initialValue, onCancel, onSave, value]);

  const keydownListener = (e: any) => {
    if (e.keyCode === KeyCodes.DOM_VK_ESCAPE) {
      onCancel();
      e.stopPropagation();
    } else if (e.keyCode === KeyCodes.DOM_VK_RETURN) {
      doSave();
      e.stopPropagation();
    }
  };

  const changeValue = (newValue: string) => {
    if (!maxLength || newValue.length <= maxLength) {
      setValue(newValue);
    }
  };

  return (
    <input
      id={id}
      ref={ref}
      role='input'
      aria-label={ariaLabel}
      className={`spot-form__input table-cell-text-box ${className || ''}`}
      type='text'
      placeholder={placeholder}
      maxLength={maxLength}
      value={value}
      readOnly={readonly}
      disabled={disabled}
      onChange={e => changeValue(e.target.value)}
      onBlur={() => doSave()}
      onKeyDown={keydownListener}
      data-testid={dataTestId}
    />
  );
};

export default TableCellTextBox;
